import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  ChevronDown,
  ChevronUp,
  Download,
  FileSpreadsheet,
  Edit2,
  Check,
  X as XIcon,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  ArrowUpDown,
  ExternalLink,
  FileText
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  useListPortfolioSitesQuery,
  useListAdminSitesQuery,
  useUpdatePortfolioSiteMutation,
  useLazyGetPortfolioSiteQuery,
  useLazyGetSiteValidationResultsQuery,
  type ValidationResults,
} from '../../redux/api/portfolioApi';
import { useGetOrganizationMembersQuery } from '../../redux/api/organizationApi';
import { useDownloadArtefactMutation, useLazyExportDocumentExcelQuery } from '../../redux/api/documentsApi';
import { setMembers } from '../../redux/slices/organizationSlice';
import { setCurrentSite } from '../../redux/slices/siteSlice';
import { formatDateTime } from '../../utils/dateUtils';
import toast from 'react-hot-toast';

interface SiteListItem {
  id: string;
  name: string;
  asbuilt_name: string | null;
  created_at: string;
  uploaded_by: string | null;
  uploader?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
  } | null;
  portfolio_id: string;
  tenant_id: string;
}

type SortField = 'name' | 'created_at' | 'status';
type SortOrder = 'asc' | 'desc';

type ValidationStatus = 'loading' | 'passed' | 'warnings' | 'errors' | 'not_available';

interface SiteValidationCache {
  [siteId: string]: {
    status: ValidationStatus;
    errorCount: number;
    warningCount: number;
  };
}

interface ValidationStatusBadgeProps {
  status: ValidationStatus;
  errorCount?: number;
  warningCount?: number;
  onClick?: () => void;
}

const ValidationStatusBadge: React.FC<ValidationStatusBadgeProps> = ({
  status,
  errorCount = 0,
  warningCount = 0,
  onClick
}) => {
  const baseClasses = "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer hover:scale-105 hover:shadow-lg";

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  switch (status) {
    case 'loading':
      return (
        <button onClick={handleClick} className={`${baseClasses} bg-surface-800 text-engramiq-bright-grey hover:bg-surface-700`}>
          <Loader2 className="w-3 h-3 animate-spin" />
          Loading
        </button>
      );
    case 'passed':
      return (
        <button onClick={handleClick} className={`${baseClasses} bg-engramiq-green/20 text-engramiq-green border border-engramiq-green/30 hover:bg-engramiq-green/30 hover:border-engramiq-green/50`}>
          <CheckCircle className="w-3.5 h-3.5" />
          <span className="font-bold">Passed</span>
        </button>
      );
    case 'warnings':
      return (
        <button onClick={handleClick} className={`${baseClasses} bg-yellow-500/20 text-yellow-500 border border-yellow-500/30 hover:bg-yellow-500/30 hover:border-yellow-500/50`}>
          <AlertTriangle className="w-3.5 h-3.5" />
          <span className="font-bold">{warningCount}</span>
          <span>Warning{warningCount !== 1 ? 's' : ''}</span>
        </button>
      );
    case 'errors':
      return (
        <button onClick={handleClick} className={`${baseClasses} bg-accent-red/20 text-accent-red border border-accent-red/30 hover:bg-accent-red/30 hover:border-accent-red/50`}>
          <XCircle className="w-3.5 h-3.5" />
          <span className="font-bold">{errorCount}</span>
          <span>Error{errorCount !== 1 ? 's' : ''}</span>
        </button>
      );
    case 'not_available':
    default:
      return (
        <button onClick={handleClick} className={`${baseClasses} bg-surface-800 text-engramiq-bright-grey border border-engramiq-green/20 hover:bg-surface-700 hover:border-engramiq-green/30`}>
          <Clock className="w-3.5 h-3.5" />
          Pending
        </button>
      );
  }
};

const SitesTableTab: React.FC = () => {
  const { state, setSelectedSite } = useAppContext();
  const dispatch = useAppDispatch();
  const [sites, setSites] = useState<SiteListItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Filter and sort states
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>('');
  const [appliedDateTo, setAppliedDateTo] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Validation cache
  const [validationCache, setValidationCache] = useState<SiteValidationCache>({});

  // Edit site name state
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editedSiteName, setEditedSiteName] = useState<string>('');
  const [savingSiteName, setSavingSiteName] = useState(false);

  // Download state
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  const authProfile = useAppSelector((s) => s.auth.profile);
  const organizationState = useAppSelector((s) => s.organization);

  const userEmail = (authProfile.email || '').toLowerCase();
  const isSuperAdmin = userEmail.endsWith('@engramiq.com');

  const rawTenant = authProfile.tenant as any;
  const userTenantId: string | null = useMemo(() => {
    if (!rawTenant) return null;
    if (typeof rawTenant === 'string') return rawTenant;
    if (typeof rawTenant === 'object') {
      return rawTenant.id || rawTenant.tenant_id || null;
    }
    return null;
  }, [rawTenant]);

  const currentOrgId = isSuperAdmin ? organizationState.currentOrganization?.id ?? null : null;
  const selectedPortfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);
  const effectiveTenantId = isSuperAdmin ? currentOrgId : userTenantId;

  // API queries
  const adminArgs = isSuperAdmin
    ? {
        ...(currentOrgId ? { organization_id: currentOrgId } : {}),
        ...(appliedDateFrom ? { uploaded_after: `${appliedDateFrom}T00:00:00` } : {}),
        ...(appliedDateTo ? { uploaded_before: `${appliedDateTo}T23:59:59` } : {}),
      }
    : undefined;

  const {
    data: adminSitesResponse,
    isLoading: adminLoading,
    error: adminSitesError,
    refetch: refetchAdminSites,
  } = useListAdminSitesQuery(adminArgs, {
    skip: !isSuperAdmin,
    refetchOnMountOrArgChange: true,
  });

  const { data: membersData } = useGetOrganizationMembersQuery(
    { tenant_id: effectiveTenantId as string, _limit: 100, _offset: 0 },
    { skip: !effectiveTenantId }
  );

  useEffect(() => {
    if (membersData) {
      dispatch(setMembers(membersData));
    }
  }, [membersData, dispatch]);

  const organizationMembers = useAppSelector((s) => s.organization.members);

  const getMemberDisplayName = (member: any): string => {
    const user = member?.user;
    if (user) {
      const first = (user.first_name || '').trim();
      const last = (user.last_name || '').trim();
      const full = [first, last].filter(Boolean).join(' ');
      if (full) return full;
      if (user.email) return user.email;
    }
    return member.user_id || '';
  };

  const getMemberEmail = (member: any): string => {
    const user = member?.user;
    if (user?.email) return user.email;
    if (typeof member.user_id === 'string') return member.user_id;
    return '';
  };

  const selectedUploaderName = useMemo(() => {
    if (selectedUser === 'all') return undefined;
    const member = organizationMembers.find((m: any) => getMemberEmail(m) === selectedUser);
    if (!member) return undefined;
    const name = getMemberDisplayName(member);
    return name || undefined;
  }, [selectedUser, organizationMembers]);

  const {
    data: portfolioSitesResponse,
    isLoading: portfolioLoading,
    error: sitesError,
    refetch: refetchPortfolioSites,
  } = useListPortfolioSitesQuery(
    selectedPortfolioId
      ? {
          portfolio_id: selectedPortfolioId,
          uploader_name: selectedUploaderName,
          uploaded_after: appliedDateFrom ? `${appliedDateFrom}T00:00:00` : undefined,
          uploaded_before: appliedDateTo ? `${appliedDateTo}T23:59:59` : undefined,
        }
      : { portfolio_id: '' },
    {
      skip: isSuperAdmin || !selectedPortfolioId,
      refetchOnMountOrArgChange: true,
    }
  );

  const loading = isSuperAdmin ? adminLoading : portfolioLoading;

  // Mutations
  const [updatePortfolioSite, { isLoading: isUpdatingSite }] = useUpdatePortfolioSiteMutation();
  const [downloadArtefact] = useDownloadArtefactMutation();
  const [triggerExportExcel] = useLazyExportDocumentExcelQuery();
  const [triggerGetPortfolioSite] = useLazyGetPortfolioSiteQuery();
  const [triggerGetValidation] = useLazyGetSiteValidationResultsQuery();

  // Transform sites data
  useEffect(() => {
    const raw = isSuperAdmin ? adminSitesResponse : portfolioSitesResponse;

    if (!raw) {
      setSites([]);
      return;
    }

    const sitesArray = Array.isArray(raw) ? raw : (raw as any)?.items || [];

    const transformedSites: SiteListItem[] = sitesArray.map((site: any) => ({
      id: site.id || site.site_id,
      name: site.name || site.site_name || 'Unknown Site',
      asbuilt_name: site.asbuilt_name || null,
      created_at: site.created_at,
      uploaded_by: site.uploaded_by || null,
      uploader: site.uploader || null,
      portfolio_id: site.portfolio_id,
      tenant_id: site.tenant_id,
    }));

    setSites(transformedSites);

    // Fetch validation status for each site
    transformedSites.forEach((site) => {
      if (!validationCache[site.id]) {
        setValidationCache((prev) => ({
          ...prev,
          [site.id]: { status: 'loading', errorCount: 0, warningCount: 0 },
        }));

        triggerGetValidation({ portfolio_id: site.portfolio_id, site_id: site.id })
          .unwrap()
          .then((result) => {
            const errorCount = result.errors?.length || 0;
            const warningCount = result.warnings?.length || 0;
            let status: ValidationStatus = 'passed';
            if (errorCount > 0) status = 'errors';
            else if (warningCount > 0) status = 'warnings';

            setValidationCache((prev) => ({
              ...prev,
              [site.id]: { status, errorCount, warningCount },
            }));
          })
          .catch(() => {
            setValidationCache((prev) => ({
              ...prev,
              [site.id]: { status: 'not_available', errorCount: 0, warningCount: 0 },
            }));
          });
      }
    });
  }, [adminSitesResponse, portfolioSitesResponse, isSuperAdmin, triggerGetValidation]);

  // Handle errors
  useEffect(() => {
    if (!sitesError) {
      setError(null);
      return;
    }
    const err = sitesError as any;
    const status = err?.status;
    if (status === 400 || status === 404) {
      setError(null);
      return;
    }
    setError('Failed to load sites');
  }, [sitesError]);

  // User filter options
  const userOptions = useMemo(() => {
    const map = new Map<string, string>();
    organizationMembers.forEach((member: any) => {
      const email = getMemberEmail(member);
      if (!email) return;
      if (!map.has(email)) {
        map.set(email, getMemberDisplayName(member));
      }
    });

    const options = Array.from(map.entries()).map(([email, label]) => ({
      value: email,
      label,
    }));

    options.sort((a, b) => a.label.localeCompare(b.label));

    return [{ value: 'all', label: 'All Users' }, ...options];
  }, [organizationMembers]);

  // Filter and sort sites
  const filteredSites = useMemo(() => {
    let result = sites
      .filter((site) =>
        selectedUser === 'all' ||
        site.uploaded_by === selectedUploaderName ||
        site.uploader === selectedUploaderName
      )
      .filter((site) => {
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          return (
            site.name.toLowerCase().includes(query) ||
            site.asbuilt_name?.toLowerCase().includes(query) ||
            site.uploaded_by?.toLowerCase().includes(query)
          );
        }
        return true;
      })
      .filter((site) => {
        if (!dateFrom && !dateTo) return true;
        const siteDate = new Date(site.created_at);
        if (dateFrom && !dateTo) return siteDate >= new Date(dateFrom);
        if (!dateFrom && dateTo) return siteDate <= new Date(dateTo + 'T23:59:59');
        if (dateFrom && dateTo) {
          return siteDate >= new Date(dateFrom) && siteDate <= new Date(dateTo + 'T23:59:59');
        }
        return true;
      });

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'status':
          const statusOrder = { errors: 0, warnings: 1, passed: 2, loading: 3, not_available: 4 };
          const aStatus = validationCache[a.id]?.status || 'not_available';
          const bStatus = validationCache[b.id]?.status || 'not_available';
          comparison = statusOrder[aStatus] - statusOrder[bStatus];
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [sites, selectedUser, selectedUploaderName, searchQuery, dateFrom, dateTo, sortField, sortOrder, validationCache]);

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleSiteSelect = async (siteId: string) => {
    setSelectedSite(siteId);
    const site = sites.find((s) => s.id === siteId);
    if (!site) return;

    const portfolioId = site.portfolio_id;
    if (!portfolioId) return;

    try {
      const fullSite = await triggerGetPortfolioSite({
        portfolio_id: portfolioId,
        site_id: siteId,
      }).unwrap();
      dispatch(setCurrentSite(fullSite));
    } catch (err) {
      console.error('Failed to fetch site details:', err);
    }
  };

  const startEditingSite = (siteId: string, currentName: string) => {
    setEditingSiteId(siteId);
    setEditedSiteName(currentName);
  };

  const cancelEditingSite = () => {
    setEditingSiteId(null);
    setEditedSiteName('');
  };

  const saveEditedSiteName = async (siteId: string) => {
    if (!editedSiteName.trim()) {
      toast.error('Site name cannot be empty');
      return;
    }

    let portfolioIdToUse: string | null = null;

    if (isSuperAdmin) {
      const site = sites.find((s) => s.id === siteId);
      if (!site || !site.portfolio_id) {
        toast.error('Site not found or missing portfolio ID');
        return;
      }
      portfolioIdToUse = site.portfolio_id;
    } else {
      if (!selectedPortfolioId) {
        toast.error('No portfolio selected');
        return;
      }
      portfolioIdToUse = selectedPortfolioId;
    }

    setSavingSiteName(true);
    try {
      const trimmedName = editedSiteName.trim();

      await updatePortfolioSite({
        portfolio_id: portfolioIdToUse,
        site_id: siteId,
        body: { name: trimmedName },
      }).unwrap();

      setSites((prevSites) =>
        prevSites.map((site) => (site.id === siteId ? { ...site, name: trimmedName } : site))
      );

      setEditingSiteId(null);
      setEditedSiteName('');
      toast.success('Site name updated successfully');
    } catch (err: any) {
      const errorMessage = err?.data?.detail || err?.message || 'Failed to update site name';
      toast.error(errorMessage);
    } finally {
      setSavingSiteName(false);
    }
  };

  const handleDownload = async (site: SiteListItem) => {
    setDownloadingDocId(site.id);
    try {
      const data = await downloadArtefact({
        artefact_id: site.id,
        document_id: site.id,
        tenant_id: site.tenant_id,
        filename: `${site.name}.pdf`,
        expires_in: 1800,
      }).unwrap();

      const downloadUrl = data.url;
      const filename = data.filename || `${site.name}.pdf`;

      if (!downloadUrl) {
        throw new Error('No download URL received');
      }

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Download started');
    } catch (err: any) {
      toast.error('Failed to download document');
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleExportExcel = async (site: SiteListItem) => {
    try {
      const blob = await triggerExportExcel(site.id).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${site.name}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Excel export downloaded');
    } catch (err: any) {
      toast.error('Failed to export to Excel');
    }
  };

  // Summary stats
  const validationStats = useMemo(() => {
    let passed = 0;
    let warnings = 0;
    let errors = 0;
    let pending = 0;

    filteredSites.forEach((site) => {
      const cache = validationCache[site.id];
      if (!cache || cache.status === 'loading' || cache.status === 'not_available') {
        pending++;
      } else if (cache.status === 'passed') {
        passed++;
      } else if (cache.status === 'warnings') {
        warnings++;
      } else if (cache.status === 'errors') {
        errors++;
      }
    });

    return { passed, warnings, errors, pending, total: filteredSites.length };
  }, [filteredSites, validationCache]);

  // Loading state
  if (loading || (!isSuperAdmin && !selectedPortfolioId)) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-engramiq-green animate-spin mx-auto mb-4" />
          <p className="text-engramiq-light-grey">Loading sites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-engramiq-dark-blue">
      {/* Header */}
      <div className="p-6 border-b border-engramiq-green/20">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-engramiq-light-grey">Sites</h1>
            <p className="text-sm text-engramiq-bright-grey mt-1">
              {filteredSites.length} site{filteredSites.length !== 1 ? 's' : ''} in portfolio
            </p>
          </div>

        </div>

        {/* Filters */}
        <div className="flex gap-4 items-end flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-engramiq-bright-grey">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-1.5 h-9 bg-surface-800 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-light-grey focus:outline-none focus:border-engramiq-green transition-all [&::-webkit-calendar-picker-indicator]:invert"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-engramiq-bright-grey">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              onBlur={(e) => {
                const value = e.target.value;
                if (value) {
                  setAppliedDateFrom(dateFrom || '');
                  setAppliedDateTo(value);
                } else {
                  setAppliedDateFrom('');
                  setAppliedDateTo('');
                }
              }}
              className="px-3 py-1.5 h-9 bg-surface-800 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-light-grey focus:outline-none focus:border-engramiq-green transition-all [&::-webkit-calendar-picker-indicator]:invert"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-engramiq-bright-grey">User</label>
            <div className="relative">
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-40 pl-3 pr-8 py-1.5 h-9 bg-surface-800 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-light-grey focus:outline-none focus:border-engramiq-green transition-all appearance-none"
              >
                {userOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-engramiq-light-grey pointer-events-none" />
            </div>
          </div>

          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label className="text-xs text-engramiq-bright-grey">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-engramiq-bright-grey" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sites..."
                className="w-full pl-10 pr-3 py-1.5 h-9 bg-surface-800 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-light-grey focus:outline-none focus:border-engramiq-green transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-accent-red/10 border border-accent-red/30 rounded-lg text-accent-red text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        {filteredSites.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-engramiq-bright-grey mx-auto mb-4" />
            <p className="text-engramiq-bright-grey text-lg mb-2">No sites found</p>
            <p className="text-engramiq-bright-grey text-sm">Try adjusting your filters or upload a new site.</p>
          </div>
        ) : (
          <div className="bg-surface-800/30 border border-engramiq-green/20 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-800/50 border-b border-engramiq-green/20">
                  <th className="text-left p-4">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center gap-2 text-xs font-semibold text-engramiq-bright-grey uppercase tracking-wider hover:text-engramiq-light-grey transition-colors"
                    >
                      Site Name
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="text-left p-4">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-2 text-xs font-semibold text-engramiq-bright-grey uppercase tracking-wider hover:text-engramiq-light-grey transition-colors"
                    >
                      Validation Status
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="text-left p-4">
                    <button
                      onClick={() => handleSort('created_at')}
                      className="flex items-center gap-2 text-xs font-semibold text-engramiq-bright-grey uppercase tracking-wider hover:text-engramiq-light-grey transition-colors"
                    >
                      Uploaded
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="text-left p-4">
                    <span className="text-xs font-semibold text-engramiq-bright-grey uppercase tracking-wider">
                      Uploaded By
                    </span>
                  </th>
                  <th className="text-right p-4">
                    <span className="text-xs font-semibold text-engramiq-bright-grey uppercase tracking-wider">
                      Actions
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredSites.map((site, index) => {
                  const isSelected = state.selectedSite === site.id;
                  const validation = validationCache[site.id] || { status: 'not_available', errorCount: 0, warningCount: 0 };

                  return (
                    <tr
                      key={site.id}
                      onClick={() => handleSiteSelect(site.id)}
                      className={`group border-b border-engramiq-green/10 cursor-pointer transition-all active:scale-[0.995] active:bg-engramiq-green/20 ${
                        isSelected
                          ? 'bg-engramiq-green/10'
                          : index % 2 === 0
                          ? 'bg-transparent hover:bg-white/5'
                          : 'bg-surface-800/20 hover:bg-white/5'
                      }`}
                    >
                      <td className="py-3 px-4">
                        {editingSiteId === site.id ? (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={editedSiteName}
                              onChange={(e) => setEditedSiteName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEditedSiteName(site.id);
                                else if (e.key === 'Escape') cancelEditingSite();
                              }}
                              className="px-2 py-1 bg-surface-800 border border-engramiq-green/30 rounded text-sm text-engramiq-light-grey focus:outline-none focus:border-engramiq-green"
                              autoFocus
                              disabled={savingSiteName}
                            />
                            <button
                              onClick={() => saveEditedSiteName(site.id)}
                              disabled={savingSiteName || isUpdatingSite}
                              className="p-1 bg-engramiq-green hover:bg-engramiq-green/80 rounded text-engramiq-dark-blue transition-all disabled:opacity-50"
                            >
                              {savingSiteName || isUpdatingSite ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={cancelEditingSite}
                              disabled={savingSiteName}
                              className="p-1 bg-surface-800 hover:bg-surface-700 rounded text-engramiq-light-grey transition-all disabled:opacity-50"
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-engramiq-light-grey">{site.name}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingSite(site.id, site.name);
                              }}
                              className="p-1 opacity-0 group-hover:opacity-100 hover:bg-engramiq-green/10 rounded text-engramiq-green/70 hover:text-engramiq-green transition-all"
                              title="Rename site"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <ValidationStatusBadge
                          status={validation.status}
                          errorCount={validation.errorCount}
                          warningCount={validation.warningCount}
                          onClick={async () => {
                            // First select the site
                            await handleSiteSelect(site.id);
                            // Then open the extraction data tab
                            window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'extractiondata' } }));
                          }}
                        />
                      </td>
                      <td className="py-2 px-4 text-sm text-engramiq-bright-grey">
                        {formatDateTime(site.created_at)}
                      </td>
                      <td className="py-2 px-4 text-sm text-engramiq-bright-grey">
                        {site.uploader?.email || site.uploaded_by || 'Unknown'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleExportExcel(site)}
                            className="p-1.5 bg-surface-800 hover:bg-surface-700 border border-engramiq-green/30 hover:border-engramiq-green rounded-lg text-engramiq-green transition-all"
                            title="Export Excel"
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SitesTableTab;
